import type { GameController } from '../core/GameController';
import type { PlayController } from '../core/PlayController';
import { pushChat, toast, ui } from '../ui/store';
import { sanitizeChat, type Emote, type PlayerInfo, type PlayerPose } from './protocol';
import { ClientSession, HostSession, type SessionListener } from './sessions';
import {
  generateRoomCode,
  isValidRoomCode,
  NetError,
  normalizeRoomCode,
  type NetworkProvider,
} from './transport';

const HOST_PLAYER_ID = 'host';
const POSE_INTERVAL_MS = 100;
const HOST_CODE_ATTEMPTS = 4;

export interface NetHooks {
  /** Identity of the local player. */
  identity(): { name: string; color: string };
  /** Called before we start mirroring someone else's farm (save ours first!). */
  beforeVisit(): void;
  /** Called after leaving a visit so the local farm can be restored. */
  restoreOwnFarm(): void;
}

/** App-facing facade over hosting, joining, chat and pose syncing. */
export class NetManager {
  private host: HostSession | null = null;
  private client: ClientSession | null = null;
  private lastPose = 0;
  private lastPoseKey = '';

  constructor(
    private readonly provider: NetworkProvider,
    private readonly game: GameController,
    private readonly play: PlayController,
    private readonly hooks: NetHooks,
  ) {}

  get active(): boolean {
    return this.host !== null || this.client !== null;
  }

  shareLink(code: string): string {
    const url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    url.searchParams.set('join', code);
    return url.toString();
  }

  async hostFarm(): Promise<void> {
    if (this.active || ui.net.value.mode === 'connecting') return;
    ui.netError.value = null;
    ui.net.value = { mode: 'connecting', role: 'host' };
    try {
      let endpoint = null;
      for (let attempt = 0; attempt < HOST_CODE_ATTEMPTS && !endpoint; attempt++) {
        try {
          endpoint = await this.provider.host(generateRoomCode());
        } catch (error) {
          if (!(error instanceof NetError && error.kind === 'code-taken')) throw error;
        }
      }
      if (!endpoint)
        throw new NetError('code-taken', 'Could not find a free farm code. Try again!');

      const { name, color } = this.hooks.identity();
      this.game.localPlayerId = HOST_PLAYER_ID;
      this.game.setMode('host');
      this.host = new HostSession(
        endpoint,
        this.game,
        { id: HOST_PLAYER_ID, name, color },
        this.listener(),
      );
      ui.net.value = { mode: 'host', code: endpoint.code };
      ui.players.value = this.host.players();
      this.play.setIdentity(name, color, true);
      pushChat({
        name: '🌼',
        color: '#ffb347',
        text: `Your farm is open! Share code ${endpoint.code} with friends.`,
        system: true,
      });
    } catch (error) {
      this.fail(error);
    }
  }

  async joinFarm(rawCode: string): Promise<void> {
    if (this.active || ui.net.value.mode === 'connecting') return;
    const code = normalizeRoomCode(rawCode);
    if (!isValidRoomCode(code)) {
      ui.netError.value = 'Farm codes are 5 letters/numbers, like "BLOOM".';
      return;
    }
    ui.netError.value = null;
    ui.net.value = { mode: 'connecting', role: 'client' };
    try {
      const connection = await this.provider.join(code);
      const { name, color } = this.hooks.identity();
      this.hooks.beforeVisit();
      const session = new ClientSession(connection, this.game, this.listener());
      this.client = session;
      const players = await session.start(name, color, (reason) => this.onClientClosed(reason));
      const hostName = players.find((p) => p.id === HOST_PLAYER_ID)?.name ?? 'a friend';
      ui.net.value = { mode: 'client', code, hostName };
      this.play.setIdentity(name, color, true);
      toast(`🧺 You're visiting ${hostName}'s farm — ${this.game.state.farmName}!`, 'good', 5);
      pushChat({
        name: '🌼',
        color: '#ffb347',
        text: `Welcome to ${this.game.state.farmName}! Press Enter to chat.`,
        system: true,
      });
    } catch (error) {
      if (this.client) {
        this.client = null;
        this.game.setMode('solo');
        this.hooks.restoreOwnFarm();
      }
      this.fail(error);
    }
  }

  leave(): void {
    if (this.host) {
      this.host.close();
      this.host = null;
      this.game.setMode('solo');
      this.game.localPlayerId = 'local';
      this.reset();
      toast('🔒 Your farm is private again.');
    } else if (this.client) {
      const session = this.client;
      this.client = null;
      session.close();
      this.backHome('You headed home. 🏡');
    }
  }

  sendChat(raw: string): void {
    const text = sanitizeChat(raw);
    if (!text || !this.active) return;
    const { name, color } = this.hooks.identity();
    pushChat({ name, color, text, system: false });
    this.play.say(null, text);
    this.host?.sendChat(text);
    this.client?.sendChat(text);
  }

  sendEmote(emote: Emote): void {
    this.play.say(null, emote);
    this.host?.sendEmote(emote);
    this.client?.sendEmote(emote);
  }

  /** Keep our name/colour in sync if the player edits them while hosting. */
  refreshIdentity(): void {
    const { name, color } = this.hooks.identity();
    this.host?.updateSelf({ name, color });
    this.play.setIdentity(name, color, this.active);
  }

  update(now: number): void {
    this.host?.update(now);
    if (!this.active || now - this.lastPose < POSE_INTERVAL_MS) return;
    this.lastPose = now;
    const pose = this.play.pose();
    const key = `${pose.x.toFixed(2)}:${pose.z.toFixed(2)}:${pose.rot.toFixed(2)}:${pose.moving}:${pose.anim ?? ''}`;
    if (key === this.lastPoseKey) return;
    this.lastPoseKey = key;
    this.sendPose(pose);
  }

  private sendPose(pose: PlayerPose): void {
    this.host?.sendPose(pose);
    this.client?.sendPose(pose);
  }

  private listener(): SessionListener {
    const nameOf = (id: string): PlayerInfo | undefined =>
      ui.players.value.find((p) => p.id === id);
    return {
      onPlayers: (players) => {
        ui.players.value = players;
        this.play.setPlayers(players, this.game.localPlayerId);
      },
      onPose: (id, pose) => this.play.setRemotePose(id, pose),
      onChat: (id, text) => {
        const player = nameOf(id);
        pushChat({
          name: player?.name ?? 'Friend',
          color: player?.color ?? '#ccc',
          text,
          system: false,
        });
        this.play.say(id, text);
      },
      onEmote: (id, emote) => this.play.say(id, emote),
      onSystem: (text) => {
        toast(text);
        pushChat({ name: '🌼', color: '#ffb347', text, system: true });
      },
      onHostLost: (reason) => {
        if (!this.host) return;
        this.host = null;
        this.game.setMode('solo');
        this.game.localPlayerId = 'local';
        this.reset();
        toast(`⚠️ ${reason}`, 'bad', 6);
      },
    };
  }

  private onClientClosed(reason: string): void {
    if (!this.client) return;
    this.client = null;
    this.backHome(reason);
  }

  private backHome(message: string): void {
    this.game.setMode('solo');
    this.game.localPlayerId = 'local';
    this.hooks.restoreOwnFarm();
    this.reset();
    toast(message);
  }

  private reset(): void {
    ui.net.value = { mode: 'solo' };
    ui.players.value = [];
    this.play.clearRemotes();
    const { name, color } = this.hooks.identity();
    this.play.setIdentity(name, color, false);
  }

  private fail(error: unknown): void {
    console.warn('Petal Plains: multiplayer error', error);
    ui.net.value = { mode: 'solo' };
    ui.netError.value =
      error instanceof Error ? error.message : 'Something went wrong. Please try again.';
  }
}
